import { useState } from 'react';
import { ArrowRight, AlertTriangle, CheckCircle2, ClipboardList, Clock3, FileSearch } from 'lucide-react';
import { Button } from '../ui/button';
import { buildEmploymentReviewExceptions } from '../../lib/employmentReviewExceptions';
import { plainEmploymentReviewAction } from '../../lib/plainRecruitmentActions';

/**
 * Exception-first admin lens over existing five source cards. No approvals,
 * requests, data writes, or bypasses; controls navigate to established forms.
 */
export default function EmploymentReviewExceptionQueue({
  snapshot,
  onOpenEvidence,
  onRetry,
  retrying = false,
  isAuditor = false,
}) {
  const [showAll, setShowAll] = useState(false);
  const view = buildEmploymentReviewExceptions(snapshot);
  if (!view.available) {
    return (
      <div role="status" className="rounded-xl border border-amber-200 bg-amber-50 p-4" data-testid="employment-exception-unavailable">
        <div className="flex gap-2 text-amber-900">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <div>
            <p className="text-sm font-semibold">Employment review needs a fresh check</p>
            <p className="mt-1 text-xs">The latest checks could not be loaded. Refresh before making a decision. Do not treat this as approval.</p>
            {onRetry && (
              <Button type="button" size="sm" variant="outline" className="mt-3 min-h-11" disabled={retrying} onClick={onRetry} data-testid="employment-exception-retry">
                {retrying ? 'Checking…' : 'Retry checks'}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const firstAdmin = view.admin.slice(0, showAll ? undefined : 1);
  const remainingAdmin = view.admin.length - firstAdmin.length;
  return (
    <section aria-label="Employment review decisions" className="rounded-xl border border-slate-200 bg-white p-4 md:p-5" data-testid="employment-exception-queue">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h4 className="flex items-center gap-2 text-base font-semibold text-slate-900">
            <ClipboardList className="h-4 w-4 text-teal-700" />
            What needs doing
          </h4>
          <p className="mt-1 text-sm text-slate-600">Start with the first item. Each check opens the person's existing record.</p>
        </div>
        {isAuditor ? <span className="rounded-full bg-slate-100 px-2 py-1 text-xs text-slate-700">Read only</span> : null}
      </div>

      <p className="mt-3 text-xs text-slate-600" data-testid="employment-review-counts">
        Your team: {view.admin.length} · Waiting for applicant: {view.applicant.length}
      </p>

      {view.admin.length > 0 ? (
        <div className="mt-4 space-y-2" data-testid="employment-exception-admin-items">
          {firstAdmin.map((item) => (
            <div key={item.key} className="flex flex-col gap-3 rounded-lg border border-amber-200 p-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="min-w-0">
                <p className="text-sm font-medium text-slate-900">{item.title}</p>
                <p className="mt-1 text-xs text-slate-600">{item.detail}</p>
                <p className="mt-1 text-xs text-amber-800">{plainEmploymentReviewAction(item.nextAction, item.nextActionLabel)}</p>
              </div>
              {onOpenEvidence && (
                <Button type="button" size="sm" variant="outline" className="min-h-11 shrink-0" onClick={() => onOpenEvidence(item.key)} data-testid={`employment-exception-open-${item.key}`}>
                  <FileSearch className="mr-1.5 h-4 w-4" /> {isAuditor ? 'View' : 'Open'} <ArrowRight className="ml-1.5 h-4 w-4" />
                </Button>
              )}
            </div>
          ))}
          {view.admin.length > 1 && (
            <Button type="button" variant="ghost" size="sm" className="min-h-11" onClick={() => setShowAll((x) => !x)}>
              {showAll ? 'Show less' : `See ${remainingAdmin} more`}
            </Button>
          )}
        </div>
      ) : (
        <p className="mt-4 rounded-lg bg-slate-50 p-3 text-sm text-slate-700" data-testid="employment-exception-no-admin">
          Nothing for your team to check here right now. Final approval still needs its usual checks.
        </p>
      )}

      {view.applicant.length > 0 && (
        <details className="mt-3 rounded-lg border border-blue-100 bg-blue-50/40" data-testid="employment-exception-applicant-items">
          <summary className="cursor-pointer px-3 py-3 text-sm font-medium text-blue-900">
            <Clock3 className="mr-1 inline h-4 w-4" />
            Waiting for applicant ({view.applicant.length})
          </summary>
          <ul className="space-y-2 border-t border-blue-100 p-3 text-sm text-blue-900">
            {view.applicant.map((item) => (
              <li key={item.key}>
                <strong>{item.title}:</strong> {item.nextActionLabel}
              </li>
            ))}
          </ul>
        </details>
      )}

      {view.unknown.length > 0 && (
        <div role="alert" className="mt-3 rounded-lg border border-amber-200 bg-amber-50 p-3" data-testid="employment-exception-unknown">
          <p className="text-sm font-medium text-amber-900">Some checks need a closer look ({view.unknown.length})</p>
          <p className="mt-1 text-xs text-amber-800">Open the person's record to see what is missing. These checks are not complete.</p>
        </div>
      )}

      {view.recorded.length > 0 && (
        <details className="mt-3 rounded-lg border border-slate-200" data-testid="employment-exception-recorded">
          <summary className="cursor-pointer px-3 py-3 text-sm font-medium text-slate-700">
            <CheckCircle2 className="mr-1 inline h-4 w-4 text-emerald-700" />
            Checks already on file ({view.recorded.length})
          </summary>
          <ul className="space-y-2 border-t border-slate-200 p-3 text-sm text-slate-700">
            {view.recorded.map((item) => <li key={item.key}><strong>{item.title}:</strong> {item.detail}</li>)}
          </ul>
          <p className="px-3 pb-3 text-xs text-slate-600">Information on file still needs the usual approval before someone can start work.</p>
        </details>
      )}
    </section>
  );
}
